import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel } from "../../components/panel/panel.jsx";
import Loader from "../../components/Loader.tsx";
import dayjs from "dayjs";
import { getAllPermissions } from "../../services/permissions.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Button from "../../components/Button.tsx";
import FilterPermissions from "../../components/permissions/FilterPermissions.tsx";
import PaginationPermissions from "../../components/permissions/PaginationPermissions.tsx";
import {
  setPermissions,
  setTotal,
  setPage,
  setSortBy,
  setDefaultSort,
} from "../../store/slices/permissionsSlice.ts";
import { fixedHeadStyle, fixedStyle } from "../../utils/constanst.js";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

interface Permission {
  id: number;
  approved: boolean;
  isRejected: boolean;
  startRequested: number;
  endRequested?: number;
  daysRequested: number;
  requireTacticRelease: boolean;
  createdAt: string;
  partner?: {
    person?: {
      firstName?: string;
      lastName?: string;
      secondLastName?: string;
    };
  };
  reviewerBy?: {
    name?: string;
    lastName?: string;
  };
}

interface PermissionParams {
  limit: number;
  offset: number;
  param: string;
  sort: string;
  order: string;
  status: string;
  permissionDate: string;
}

interface TableColumn {
  key: string;
  label: string;
  sortable: boolean;
  render: (permission: Permission) => React.ReactNode;
}

const SORTABLE_COLUMNS = {
  ID: "id",
  PARTNER: "partner",
  START_REQUESTED: "startRequested",
  DAYS_REQUESTED: "daysRequested",
  STATUS: "status",
  REVIEWER: "reviewer",
  TACTIC_RELEASE: "requireTacticRelease",
} as const;

const formatDate = (timestamp: number): string => {
  if (!timestamp) return "-";
  return moment.unix(timestamp).format("ddd DD MMM YYYY");
};

const StatusBadge = ({ permission }: { permission: Permission }) => {
  const { approved, isRejected } = permission;

  let bg = "#fef9c3";
  let color = "#854d0e";
  let label = "Pendiente";

  if (approved) {
    bg = "#bbf7d0";
    color = "#166534";
    label = "Aprobado";
  } else if (isRejected) {
    bg = "#fee2e2";
    color = "#b91c1c";
    label = "Rechazado";
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.20rem 0.7rem",
        borderRadius: "999px",
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.03em",
        backgroundColor: bg,
        color,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const TableHeader = ({
  column,
  label,
  sortBy,
  defaultSort,
  onSort,
  isSortable = false,
}: {
  column: string;
  label: string;
  sortBy: string;
  defaultSort: string;
  onSort: (column: string) => void;
  isSortable?: boolean;
}) => {
  const getIconSort = () => (
    <i
      className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
    />
  );

  if (!isSortable) {
    return <th className="text-center">{label}</th>;
  }

  return (
    <th
      style={{ ...fixedStyle } as any}
      className="text-center cursor-pointer"
      onClick={() => onSort(column)}
    >
      {label} {sortBy === column && getIconSort()}
    </th>
  );
};

const actionButtonStyle: React.CSSProperties = {
  borderRadius: "999px",
  width: 32,
  height: 32,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};

const ActionButtons = ({
  permission,
  onReview,
}: {
  permission: Permission;
  onReview: (permission: Permission) => void;
}) => (
  <div className="d-flex justify-content-center align-items-center">
    <button
      type="button"
      className="btn btn-azul btn-sm"
      style={actionButtonStyle}
      title="Revisar permiso"
      onClick={() => onReview(permission)}
    >
      <i className="fa-solid fa-eye" />
    </button>
  </div>
);

const getTableColumns = (): TableColumn[] => [
  {
    key: SORTABLE_COLUMNS.ID,
    label: "Id",
    sortable: true,
    render: (permission) => `${permission.id}`,
  },
  {
    key: SORTABLE_COLUMNS.PARTNER,
    label: "Colaborador",
    sortable: true,
    render: (permission) =>
      permission.partner?.person
        ? toCamelCase(
            `${permission.partner.person.firstName || ""} ${
              permission.partner.person.lastName || ""
            } ${permission.partner.person.secondLastName || ""}`
          )
        : "-",
  },
  {
    key: SORTABLE_COLUMNS.START_REQUESTED,
    label: "Fecha permiso",
    sortable: true,
    render: (permission) => formatDate(permission.startRequested),
  },
 
  {
    key: SORTABLE_COLUMNS.STATUS,
    label: "Estado",
    sortable: true,
    render: (permission) => <StatusBadge permission={permission} />,
  },
  {
    key: SORTABLE_COLUMNS.REVIEWER,
    label: "Revisado por",
    sortable: true,
    render: (permission) =>
      permission.reviewerBy
        ? `${permission.reviewerBy.name || ""} ${
            permission.reviewerBy.lastName || ""
          }`.trim() || "-"
        : "-",
  },
  // {
  //   key: SORTABLE_COLUMNS.TACTIC_RELEASE,
  //   label: "Necesita liberar táctica",
  //   sortable: true,
  //   render: (permission) => (permission.requireTacticRelease ? "Sí" : "No"),
  // },
  {
    key: "actions",
    label: "Acciones",
    sortable: false,
    render: (permission) => null,
  },
];

const AllPermissions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  useAppSettings();

  const { permissions, total, page, limit, query, sortBy, defaultSort } =
    useSelector((state: RootState) => state.permissions);

  const { bossId } = useParams();

  const permissionParams = useMemo((): PermissionParams & { bossId?: number } => {
    const offset = page * limit;
    const param = query.includes("param=")
      ? query.split("param=")[1]?.split("&")[0] || ""
      : "";
    const status = query.includes("status=")
      ? query.split("status=")[1]?.split("&")[0] || "all"
      : "all";
    const permissionDate = query.includes("permissionDate=")
      ? query.split("permissionDate=")[1]?.split("&")[0] || ""
      : "";

    const params: PermissionParams & { bossId?: number } = {
      limit,
      offset,
      param,
      sort: sortBy,
      order: defaultSort.toUpperCase(),
      status,
      permissionDate,
    };

    if (bossId) {
      params.bossId = Number(bossId);
    }

    return params;
  }, [page, limit, query, sortBy, defaultSort, bossId]);

  const tableColumns = useMemo(() => getTableColumns(), []);

  const fetchPermissions = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAllPermissions(permissionParams);
      dispatch(setPermissions(response.data || []));
      dispatch(setTotal(response.total || 0));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Error desconocido al cargar permisos";
      setError(errorMessage);
      console.error("Error fetching permissions:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, permissionParams]);

  const handleSort = useCallback(
    (column: string): void => {
      const sortOrder =
        sortBy !== column ? "asc" : defaultSort === "asc" ? "desc" : "asc";

      dispatch(setSortBy(column));
      dispatch(setPage(0));
      dispatch(setDefaultSort(sortOrder));
    },
    [dispatch, sortBy, defaultSort]
  );

  const handleReview = useCallback(
    (permission: Permission): void => {
      navigate(`/colaboradores/aprobar-permiso/${permission.id}`);
    },
    [navigate]
  );

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const renderTableRow = useCallback(
    (permission: Permission) => (
      <tr key={permission.id}>
        {tableColumns.map((column) => (
          <td key={column.key} className="text-center">
            {column.key === "actions" ? (
              <ActionButtons
                permission={permission}
                onReview={handleReview}
              />
            ) : (
              column.render(permission)
            )}
          </td>
        ))}
      </tr>
    ),
    [tableColumns, handleReview]
  );

  const renderTableHeaders = useCallback(
    () => (
      <tr style={{ ...fixedHeadStyle } as any}>
        {tableColumns.map((column) => (
          <TableHeader
            key={column.key}
            column={column.key}
            label={column.label}
            sortBy={sortBy}
            defaultSort={defaultSort}
            onSort={handleSort}
            isSortable={column.sortable}
          />
        ))}
      </tr>
    ),
    [tableColumns, sortBy, defaultSort, handleSort]
  );

  const renderEmptyState = useCallback(
    () => (
      <tr>
        <td
          colSpan={tableColumns.length}
          className="text-center text-muted py-4"
        >
          <div
            className="py-4 justify-content-center align-items-center"
            style={{ height: "450px" }}
          >
            <h5 className="text-secondary text-center fs-20px">
              {error ? (
                <div>
                  <i className="fa fa-exclamation-triangle text-warning me-2"></i>
                  Error al cargar permisos: {error}
                </div>
              ) : (
                "Aún no existen permisos registrados"
              )}
            </h5>
            {!error && (
              <>
                <h5 className="text-center text-secondary fw-400 fs-15px">
                  Aqui podras revisar los permisos de los colaboradores
                </h5>
                <h5 className="text-center text-secondary fw-400">
                  y aprobarlos
                </h5>
              </>
            )}
          </div>
        </td>
      </tr>
    ),
    [error, tableColumns.length]
  );

  return (
    <Layout>
      <Title
        baseTitle="Permisos"
        basePath="/colaboradores/permisos"
        title="Listado de permisos"
      />

      <div className="card border-0 m-4">
        <div className="tab-pane fade show active" id="sucursal">
          <div className="card-body">
            <Panel>
              <div className="input-group w-100">
                <div className="d-flex align-items-center w-100">
                  <FilterPermissions />
                </div>
              </div>

              <div
                className="table-responsive mb-3"
                style={{ overflowY: "auto", height: "500px" }}
              >
                <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                  <thead>{renderTableHeaders()}</thead>
                  <tbody>
                    {permissions?.length > 0
                      ? permissions.map(renderTableRow)
                      : renderEmptyState()}
                  </tbody>
                </table>
              </div>

              <PaginationPermissions />
            </Panel>
          </div>
        </div>
      </div>

      <Loader isLoading={loading} />
    </Layout>
  );
};

export default AllPermissions;
